import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { UserStorageService } from '@proget-shared/storage';
import { BehaviorSubject, map, Observable, shareReplay, startWith, Subject, switchMap } from 'rxjs';

import { Attribute, User } from '../model';
import { UserCardRepository } from '../user-card.repository';

@Injectable()
export class UserCardService {
  public readonly user$: Observable<User>;
  public readonly allAttributes$: Observable<Attribute[]>;
  public readonly visibleAttributes$: Observable<Attribute[]>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly userCardRepository = inject(UserCardRepository);
  private readonly userStorageService = inject(UserStorageService, { optional: true });
  private readonly allAttributesSubject = new BehaviorSubject<Attribute[]>([]);
  private readonly reloadSubject = new Subject<void>();
  private readonly visibleAttributesSubject = new BehaviorSubject<Attribute[]>([]);

  private user: User;

  constructor() {
    const activatedRoute = inject(ActivatedRoute);

    this.user$ = activatedRoute.data
      .pipe(
        map((data) => data.user),
        switchMap((user) => this.reloadSubject.pipe(
          switchMap(() => this.userCardRepository.getUser(user.uuid)),
          startWith(user)
        )),
        takeUntilDestroyed(this.destroyRef),
        shareReplay(1)
      );

    this.allAttributes$ = this.allAttributesSubject.asObservable();
    this.visibleAttributes$ = this.visibleAttributesSubject.asObservable();

    this.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.user = user;
          this.loadAttributes(user);
        },
      });

    this.destroyRef.onDestroy(() => {
      this.allAttributesSubject.complete();
      this.visibleAttributesSubject.complete();
    });
  }

  public attributesVisibilityChanged(attributes: Attribute[]): void {
    this.visibleAttributesSubject.next(attributes.filter((attribute) => attribute.isVisibleControl.value));
    this.saveVisibleAttributes(attributes);
  }

  public reloadUser(): void {
    this.reloadSubject.next();
  }

  public reloadAttributes(): void {
    this.loadAttributes(this.user);
  }

  private isUserStorageEnabled(): boolean {
    return this.userStorageService?.isUserSet();
  }

  private saveVisibleAttributes(attributes: Attribute[]): void {
    if (this.isUserStorageEnabled()) {
      const key = 'user-card-attributes';

      const attributesConfiguration = attributes
        .filter((attribute) => attribute.isVisibleControl.value)
        .reduce(
          (configuration, attribute) => Object.assign(configuration, { [attribute.key]: attribute.isVisibleControl.value }),
          {}
        );

      this.userStorageService.set(key, attributesConfiguration);
    }
  }

  private updateAttributesFromConfiguration(attributes: Attribute[]): Attribute[] {
    if (!this.isUserStorageEnabled()) {
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const savedConfiguration = this.userStorageService.get('user-card-attributes') ?? { 'User:Email': true }; // Default attribute
    const configuredAttributes = Object.keys(savedConfiguration);

    return attributes.map((attribute) => {
      if (configuredAttributes.includes(attribute.key)) {
        attribute.isVisibleControl.setValue(savedConfiguration[attribute.key], { emitEvent: false });
      }

      return attribute;
    });
  }

  private loadAttributes(user: User): void {
    this.userCardRepository
      .getAttributes(user.uuid)
      .subscribe({
        next: (attributes) => {
          const updatedAttributes = this.updateAttributesFromConfiguration(attributes);

          this.allAttributesSubject.next(updatedAttributes);
          this.visibleAttributesSubject.next(updatedAttributes.filter((attribute) => attribute.isVisibleControl.value));
        },
        error: () => {
          this.allAttributesSubject.next([]);
          this.visibleAttributesSubject.next([]);
        },
      });
  }
}
